/**
 * Facilities functional pass: the physical plant — buildings, spaces,
 * equipment, the requests people make about them and the work orders that
 * fix them.
 *
 * Facilities owns buildings, spaces, work orders, maintenance and service
 * history. Assets owns an asset's lifecycle (Facilities links to it and
 * never copies it), Purchasing owns procurement and money, Visitors owns a
 * vendor's physical check-in, Operations and Emergency consume facility
 * status, Calendar consumes room availability.
 *
 * A request is what somebody reported; a work order is the work Facilities
 * accepted. They are different records: one request can become a work
 * order, be linked to an existing one, be returned, or be rejected, and the
 * requester still sees their own report either way.
 *
 * Priority (how soon) and operational impact (what it does to the day) are
 * separate fields. Neither is inferred: people set them, Settings defines
 * them.
 */

import type { BaseDocument } from '../platform/documents.js'

// ─────────────────────────────────────────────────────────────────────────────
// Categories, priorities, impact
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The default category keys. The district can relabel, add or retire them in
 * Settings; a work order stores the key, so a renamed category never rewrites
 * history. `technology`, `furniture` and `safety` are the original keys.
 */
export const WORK_ORDER_CATEGORIES = [
  'hvac',
  'plumbing',
  'electrical',
  'carpentry',
  'custodial',
  'grounds',
  'roofing',
  'building_envelope',
  'kitchen_equipment',
  'security_hardware',
  'doors_locks',
  'painting',
  'pest_control',
  'elevator',
  'fire_life_safety',
  'technology',
  'furniture',
  'safety',
  'event_setup',
  'other',
] as const
export type WorkOrderCategory = (typeof WORK_ORDER_CATEGORIES)[number]
export const WORK_ORDER_CATEGORY_LABELS: Record<WorkOrderCategory, string> = {
  hvac: 'HVAC',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  carpentry: 'Carpentry',
  custodial: 'Custodial',
  grounds: 'Grounds',
  roofing: 'Roofing',
  building_envelope: 'Building envelope',
  kitchen_equipment: 'Kitchen equipment',
  security_hardware: 'Security hardware',
  doors_locks: 'Doors / locks',
  painting: 'Painting',
  pest_control: 'Pest control',
  elevator: 'Elevator',
  fire_life_safety: 'Fire / life safety',
  technology: 'Technology',
  furniture: 'Furniture',
  safety: 'Safety',
  event_setup: 'Event setup',
  other: 'Other',
}

/** Priority is how soon. The district defines what each level means (and its targets) in Settings. */
export const WORK_ORDER_PRIORITIES = ['routine', 'normal', 'high', 'urgent', 'critical'] as const
export type WorkOrderPriority = (typeof WORK_ORDER_PRIORITIES)[number]
export const WORK_ORDER_PRIORITY_LABELS: Record<WorkOrderPriority, string> = { routine: 'Routine', normal: 'Normal', high: 'High', urgent: 'Urgent', critical: 'Critical' }
export const PRIORITY_RANK: Record<WorkOrderPriority, number> = { critical: 0, urgent: 1, high: 2, normal: 3, routine: 4 }

/** Operational impact is what the problem does to the day. It feeds Operations; priority does not. */
export const OPERATIONAL_IMPACTS = [
  'none',
  'minor',
  'room_unavailable',
  'area_unavailable',
  'instruction_disrupted',
  'food_service_impact',
  'transportation_impact',
  'safety_impact',
  'building_closure_risk',
  'building_closed',
] as const
export type OperationalImpact = (typeof OPERATIONAL_IMPACTS)[number]
export const OPERATIONAL_IMPACT_LABELS: Record<OperationalImpact, string> = {
  none: 'None',
  minor: 'Minor',
  room_unavailable: 'Room unavailable',
  area_unavailable: 'Area unavailable',
  instruction_disrupted: 'Instruction disrupted',
  food_service_impact: 'Food service impact',
  transportation_impact: 'Transportation impact',
  safety_impact: 'Safety impact',
  building_closure_risk: 'Building closure risk',
  building_closed: 'Building closed',
}
/** Impacts that make a space unavailable — these publish a closure Calendar and Operations can read. */
export const CLOSING_IMPACTS: readonly OperationalImpact[] = ['room_unavailable', 'area_unavailable', 'building_closed']
/** Impacts Operations treats as part of the day (everything but none/minor). */
export const OPERATIONAL_IMPACTS_THAT_MATTER: readonly OperationalImpact[] = OPERATIONAL_IMPACTS.filter((i) => i !== 'none' && i !== 'minor')

export const UTILITIES = ['power', 'water', 'gas', 'sewer', 'internet', 'hvac', 'generator', 'other'] as const
export type Utility = (typeof UTILITIES)[number]
export const UTILITY_LABELS: Record<Utility, string> = { power: 'Electric', water: 'Water', gas: 'Gas', sewer: 'Sewer', internet: 'Internet / communications', hvac: 'HVAC', generator: 'Generator', other: 'Other' }

// ─────────────────────────────────────────────────────────────────────────────
// Requests
// ─────────────────────────────────────────────────────────────────────────────

/** Where a report came from. Preserved on the request and on any work order it becomes. */
export const REQUEST_SOURCES = [
  'teacher',
  'staff',
  'administrator',
  'operations',
  'emergency',
  'cafeteria',
  'transportation',
  'health_office',
  'athletics',
  'library',
  'assets',
  'discipline',
  'preventive_maintenance',
  'inspection',
  'integration',
] as const
export type RequestSource = (typeof REQUEST_SOURCES)[number]
export const REQUEST_SOURCE_LABELS: Record<RequestSource, string> = {
  teacher: 'Teacher',
  staff: 'Staff',
  administrator: 'Administrator',
  operations: 'Operations',
  emergency: 'Emergency',
  cafeteria: 'Cafeteria',
  transportation: 'Transportation',
  health_office: 'Health Office',
  athletics: 'Athletics',
  library: 'Library',
  assets: 'Assets',
  discipline: 'Discipline',
  preventive_maintenance: 'Preventive maintenance',
  inspection: 'Inspection',
  integration: 'Automated system',
}

export const FACILITY_REQUEST_STATUSES = ['submitted', 'under_review', 'accepted', 'converted', 'returned', 'duplicate', 'rejected', 'closed'] as const
export type FacilityRequestStatus = (typeof FACILITY_REQUEST_STATUSES)[number]
export const FACILITY_REQUEST_STATUS_LABELS: Record<FacilityRequestStatus, string> = {
  submitted: 'Submitted',
  under_review: 'Under review',
  accepted: 'Accepted',
  converted: 'Converted to work order',
  returned: 'Returned for information',
  duplicate: 'Duplicate',
  rejected: 'Rejected',
  closed: 'Closed',
}
/** A request still waiting on Facilities (or, when returned, on the requester). */
export const OPEN_FACILITY_REQUEST_STATUSES: readonly FacilityRequestStatus[] = ['submitted', 'under_review', 'accepted', 'returned']

/** Who did what to a request or work order — the timeline is the record. */
export interface FacilityEvent {
  at: string
  byName: string
  byUserId?: string | null
  /** What happened, in words ("Assigned to Michael Smith"). */
  action: string
  /** The status after the event, when it changed one. */
  status?: string
  note?: string
  /** Requester-visible events are the only ones the requester portal shows. */
  public?: boolean
}

export interface FacilityRequest extends BaseDocument {
  docType: 'facilityRequest'
  requestNumber: string
  schoolId: string | null
  /** The building node in the location directory. */
  buildingId: string | null
  locationId: string | null
  /** What the requester called the place, kept even when a location is picked. */
  locationText: string
  /** Names as they were when reported — a renamed room never rewrites a report. */
  buildingName: string | null
  locationName: string | null
  category: string
  title: string
  description: string
  severity: WorkOrderPriority | null
  operationalImpact: OperationalImpact
  equipmentId: string | null
  assetId: string | null
  requesterUserId: string
  requesterStaffId: string | null
  requesterName: string
  contact: string | null
  preferredAccess: string | null
  source: RequestSource
  sourceModule: string | null
  sourceEntityId: string | null
  status: FacilityRequestStatus
  /** The work order this request became or was linked to. */
  workOrderId: string | null
  workOrderNumber: string | null
  /** What Facilities asked the requester (returned) or why it was rejected. */
  responseNote: string | null
  photoCount: number
  idempotencyKey?: string | null
  history: FacilityEvent[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Work orders
// ─────────────────────────────────────────────────────────────────────────────

export const WORK_ORDER_STATUSES = [
  'new',
  'triaged',
  'assigned',
  'in_progress',
  'waiting_parts',
  'waiting_vendor',
  'waiting_access',
  'on_hold',
  'work_complete',
  'awaiting_verification',
  'closed',
  'cancelled',
  'unable_to_resolve',
] as const
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number]
export const WORK_ORDER_STATUS_LABELS: Record<WorkOrderStatus, string> = {
  new: 'New',
  triaged: 'Triaged',
  assigned: 'Assigned',
  in_progress: 'In progress',
  waiting_parts: 'Waiting for parts',
  waiting_vendor: 'Waiting for vendor',
  waiting_access: 'Waiting for access',
  on_hold: 'On hold',
  work_complete: 'Work complete',
  awaiting_verification: 'Awaiting verification',
  closed: 'Closed',
  cancelled: 'Cancelled',
  unable_to_resolve: 'Unable to resolve',
}

/** The original vocabulary, still on records written before this pass. */
export const LEGACY_WORK_ORDER_STATUSES = ['submitted', 'waiting', 'complete'] as const
const LEGACY_STATUS_MAP: Record<string, WorkOrderStatus> = { submitted: 'new', waiting: 'on_hold', complete: 'closed' }

/** Any stored status, old or new, as the current vocabulary. */
export function normalizeWorkOrderStatus(status: string | null | undefined): WorkOrderStatus {
  if (!status) return 'new'
  if ((WORK_ORDER_STATUSES as readonly string[]).includes(status)) return status as WorkOrderStatus
  return LEGACY_STATUS_MAP[status] ?? 'new'
}

const LEGACY_PRIORITY_MAP: Record<string, WorkOrderPriority> = { low: 'routine' }
export function normalizeWorkOrderPriority(priority: string | null | undefined): WorkOrderPriority {
  if (priority && (WORK_ORDER_PRIORITIES as readonly string[]).includes(priority)) return priority as WorkOrderPriority
  return (priority && LEGACY_PRIORITY_MAP[priority]) || 'normal'
}

/** Finished as far as the record goes — nothing more will happen without a reopen. */
export const TERMINAL_WORK_ORDER_STATUSES: readonly WorkOrderStatus[] = ['closed', 'cancelled', 'unable_to_resolve']
/** The repair is done (or the order is over): the space no longer needs to be held for it. */
export const REPAIR_DONE_STATUSES: readonly WorkOrderStatus[] = ['work_complete', 'awaiting_verification', 'closed', 'cancelled', 'unable_to_resolve']
export const WAITING_STATUSES: readonly WorkOrderStatus[] = ['waiting_parts', 'waiting_vendor', 'waiting_access', 'on_hold']

/** Still open as a record (verification counts as open). Safe on legacy statuses. */
export const isOpenWorkOrder = (status: string | null | undefined): boolean => !TERMINAL_WORK_ORDER_STATUSES.includes(normalizeWorkOrderStatus(status))
/** Work is still outstanding — the problem is not fixed yet. Safe on legacy statuses. */
export const isActiveWorkOrder = (status: string | null | undefined): boolean => !REPAIR_DONE_STATUSES.includes(normalizeWorkOrderStatus(status))
/** Urgent enough for Home and My Work rows ("urgent" in the original vocabulary). */
export const isUrgentPriority = (priority: string | null | undefined): boolean => {
  const p = normalizeWorkOrderPriority(priority)
  return p === 'urgent' || p === 'critical'
}

/**
 * The workflow as data: a work order offers exactly the next steps its status
 * allows. Reopen (closed → in progress) and verification are their own
 * actions with their own rules, so they are not here.
 */
export const WORK_ORDER_TRANSITIONS: Record<WorkOrderStatus, readonly WorkOrderStatus[]> = {
  new: ['triaged', 'assigned', 'on_hold', 'cancelled'],
  triaged: ['assigned', 'on_hold', 'cancelled'],
  assigned: ['in_progress', 'waiting_access', 'on_hold', 'cancelled'],
  in_progress: ['waiting_parts', 'waiting_vendor', 'waiting_access', 'on_hold', 'work_complete', 'unable_to_resolve'],
  waiting_parts: ['in_progress', 'on_hold', 'unable_to_resolve', 'cancelled'],
  waiting_vendor: ['in_progress', 'work_complete', 'on_hold', 'unable_to_resolve', 'cancelled'],
  waiting_access: ['in_progress', 'assigned', 'on_hold', 'cancelled'],
  on_hold: ['in_progress', 'assigned', 'unable_to_resolve', 'cancelled'],
  work_complete: ['awaiting_verification', 'closed', 'in_progress'],
  awaiting_verification: ['closed', 'in_progress'],
  closed: [],
  cancelled: [],
  unable_to_resolve: [],
}

export const UNABLE_REASONS = ['replacement_needed', 'vendor_required', 'capital_project_required', 'parts_unavailable', 'end_of_life', 'access_unavailable', 'other'] as const
export type UnableReason = (typeof UNABLE_REASONS)[number]
export const UNABLE_REASON_LABELS: Record<UnableReason, string> = {
  replacement_needed: 'Replacement needed',
  vendor_required: 'Vendor required',
  capital_project_required: 'Capital project required',
  parts_unavailable: 'Parts unavailable',
  end_of_life: 'End of life',
  access_unavailable: 'Access unavailable',
  other: 'Other',
}

export const VERIFICATION_ACTIONS = ['confirm', 'reopen', 'needs_more_work', 'unable_to_verify'] as const
export type VerificationAction = (typeof VERIFICATION_ACTIONS)[number]
export const VERIFICATION_ACTION_LABELS: Record<VerificationAction, string> = { confirm: 'Confirm resolved', reopen: 'Reopen', needs_more_work: 'Needs more work', unable_to_verify: 'Unable to verify' }

export const WORK_CLASSES = ['maintenance', 'capital', 'custodial', 'grounds', 'project'] as const
export type WorkClass = (typeof WORK_CLASSES)[number]

/** A link to the record that caused this work (an Operations issue, a vehicle defect, an incident…). */
export interface FacilitySourceLink {
  module: string
  entityId: string
  label: string
  to: string | null
}

/** One completion, kept even when the order is reopened — a reopen never erases the earlier repair. */
export interface WorkCompletion {
  at: string
  byName: string
  resolution: string
  verifiedAt?: string | null
  verifiedByName?: string | null
  outcome?: 'verified' | 'reopened' | 'needs_more_work' | 'auto_closed' | 'closed_without_verification' | null
}

export interface WorkOrder extends BaseDocument {
  docType: 'workOrder'
  workOrderNumber?: string | null
  title?: string | null
  /** Free-text location as reported. Kept for every reader written before the directory existed. */
  location: string
  category: string
  description: string
  priority: string
  status: string
  operationalImpact?: OperationalImpact | null
  /** The room or area affected, as people say it ("Gym"). */
  affectedArea?: string | null
  /** A utility outage this order tracks. */
  utility?: Utility | null
  // ── where ──
  schoolId?: string | null
  buildingId?: string | null
  locationId?: string | null
  /** Every space a building-wide or multi-room problem touches — one order, not one per room. */
  affectedLocationIds?: string[]
  /** Names at the time — a room renamed later never rewrites the record. */
  buildingName?: string | null
  locationName?: string | null
  // ── what ──
  equipmentId?: string | null
  equipmentName?: string | null
  assetId?: string | null
  workClass?: WorkClass | null
  // ── who asked ──
  requestId?: string | null
  requestNumber?: string | null
  /** Everyone who reported this problem (a merged duplicate adds its requester here). */
  requesterUserIds?: string[]
  submittedByStaffId: string
  submittedByName: string
  source?: RequestSource | null
  sourceLink?: FacilitySourceLink | null
  // ── who has it ──
  /** A team key from Settings (hvac, plumbing, general…). */
  assignedTeam?: string | null
  assignedStaffIds?: string[]
  /** Display names of the assignees, joined. Staff reports and offboarding read this. */
  assignedToName?: string | null
  vendorId?: string | null
  vendorName?: string | null
  // ── when ──
  responseDueAt?: string | null
  dueAt?: string | null
  triagedAt?: string | null
  assignedAt?: string | null
  startedAt?: string | null
  workCompletedAt?: string | null
  verifiedAt?: string | null
  closedAt?: string | null
  /** When the current waiting/on-hold stretch began — "waiting since". */
  statusSince?: string | null
  // ── outcome ──
  resolutionNote?: string | null
  completions?: WorkCompletion[]
  reopenCount?: number
  verificationRequired?: boolean
  verifierUserIds?: string[]
  unableReason?: UnableReason | null
  /** Records this order was converted into when it could not be resolved here. */
  conversions?: { kind: 'purchase_request' | 'project' | 'asset_replacement' | 'vendor_service'; id: string | null; label: string; at: string }[]
  /** The closure this order published, while its space is unavailable. */
  closureId?: string | null
  /** Escalation steps already sent (rule key), so a sweep never sends one twice. */
  escalationsSent?: string[]
  // ── totals (from labor/part entries; costs only where someone recorded one) ──
  laborHours?: number
  laborCostCents?: number | null
  partsCostCents?: number | null
  photoBlobPath?: string | null
  idempotencyKey?: string | null
  /** The legacy record kept only `status`; current events carry an action. */
  history: { at: string; status: string; byName: string; note?: string; action?: string; byUserId?: string | null; public?: boolean }[]
  // ── stage 2: recurring work, purchasing, vendors, projects ──
  pmInstanceId?: string | null
  inspectionInstanceId?: string | null
  projectId?: string | null
  /** Purchasing requests raised from this order (Purchasing owns their status). */
  purchaseRequestIds?: string[]
  vendorStatus?: 'requested' | 'scheduled' | 'onsite' | 'work_complete' | 'invoice_pending' | 'closed' | null
  vendorScheduledAt?: string | null
  /** A vendor quote is a reference here; Purchasing approves the money. */
  vendorQuote?: { reference: string; amountCents: number | null; receivedAt: string; purchaseRequestId: string | null } | null
  warrantyAcknowledgedAt?: string | null
  utilityIssueType?: string | null
  /** The Operations issue an escalation or failure raised, when one was. */
  opsIssueId?: string | null
  /** Future building-automation link (no provider is connected). */
  externalSystemId?: string | null
  providerType?: string | null
  lastSyncedAt?: string | null
  providerStatus?: string | null
}

export const NOTE_TYPES = ['technician', 'diagnostic', 'repair', 'internal', 'requester', 'vendor'] as const
export type NoteType = (typeof NOTE_TYPES)[number]
export const NOTE_TYPE_LABELS: Record<NoteType, string> = { technician: 'Technician note', diagnostic: 'Diagnostic', repair: 'Repair', internal: 'Internal', requester: 'Requester-visible', vendor: 'Vendor note' }
/** Only requester notes leave Facilities. Everything else is internal by default. */
export const noteVisibility = (type: NoteType): 'internal' | 'requester' => (type === 'requester' ? 'requester' : 'internal')

export interface WorkOrderNote extends BaseDocument {
  docType: 'workOrderNote'
  workOrderId: string
  noteType: NoteType
  visibility: 'internal' | 'requester'
  content: string
  authorName: string
  authorUserId: string
  idempotencyKey?: string | null
}

export const PHOTO_PHASES = ['report', 'before', 'during', 'after'] as const
export type PhotoPhase = (typeof PHOTO_PHASES)[number]

/** A photo on a request or a work order. Blob path, never a URL — links are minted per read. */
export interface FacilityPhoto extends BaseDocument {
  docType: 'facilityPhoto'
  requestId: string | null
  workOrderId: string | null
  phase: PhotoPhase
  blobPath: string
  contentType: string
  caption: string | null
  takenByName: string
  takenByUserId: string
  /** A requester's report photo is visible to them; work photos are internal. */
  visibility: 'internal' | 'requester'
}

export const LABOR_TYPES = ['regular', 'overtime', 'diagnostic', 'travel', 'callout', 'contract_oversight'] as const
export type LaborType = (typeof LABOR_TYPES)[number]
export const LABOR_TYPE_LABELS: Record<LaborType, string> = { regular: 'Regular', overtime: 'Overtime', diagnostic: 'Diagnostic', travel: 'Travel', callout: 'Call-out', contract_oversight: 'Contractor oversight' }

/** Time someone spent on a work order. Not payroll: a payroll reference may point at it. */
export interface LaborEntry extends BaseDocument {
  docType: 'workOrderLabor'
  workOrderId: string
  staffId: string
  staffName: string
  workDate: string
  startTime: string | null
  endTime: string | null
  hours: number
  laborType: LaborType
  overtime: boolean
  notes: string | null
  payrollReference: string | null
  /** Rate from Settings at the time it was logged, when the district configured one. */
  rateCents: number | null
  enteredByName: string
  idempotencyKey?: string | null
}

export const WO_PART_STATUSES = ['needed', 'reserved', 'requested', 'ordered', 'received', 'used', 'returned'] as const
export type WorkOrderPartStatus = (typeof WO_PART_STATUSES)[number]
export const WO_PART_STATUS_LABELS: Record<WorkOrderPartStatus, string> = { needed: 'Needed', reserved: 'Reserved', requested: 'Purchase requested', ordered: 'Ordered', received: 'Received', used: 'Used', returned: 'Returned' }

export interface WorkOrderPart extends BaseDocument {
  docType: 'workOrderPart'
  workOrderId: string
  partId: string | null
  description: string
  quantity: number
  unit: string | null
  unitCostCents: number | null
  inventorySource: 'stock' | 'purchase' | 'vendor' | 'other'
  status: WorkOrderPartStatus
  /** The Purchasing request / order this part rides on. Purchasing owns their status. */
  purchaseRequestId: string | null
  purchaseRequestNumber: string | null
  addedByName: string
  usedAt: string | null
  idempotencyKey?: string | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Buildings & spaces (the location directory)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Every node in the directory. `school`/`area`/`stadium`/`admin`/`kitchen`
 * are the original kinds and still read; new nodes use the hierarchy
 * campus → building → floor → wing → room (or an outdoor/athletic node).
 */
export const LOCATION_KINDS = [
  'campus',
  'building',
  'floor',
  'wing',
  'room',
  'outdoor',
  'athletic',
  'parking',
  'field',
  'warehouse',
  'depot',
  'other',
  // original kinds
  'school',
  'area',
  'stadium',
  'admin',
  'kitchen',
] as const
export type LocationKind = (typeof LOCATION_KINDS)[number]
export const LOCATION_KIND_LABELS: Record<LocationKind, string> = {
  campus: 'Campus',
  building: 'Building',
  floor: 'Floor',
  wing: 'Wing',
  room: 'Room / space',
  outdoor: 'Outdoor area',
  athletic: 'Athletic facility',
  parking: 'Parking lot',
  field: 'Field',
  warehouse: 'Warehouse',
  depot: 'Transportation depot',
  other: 'Other',
  school: 'School',
  area: 'Area',
  stadium: 'Stadium',
  admin: 'Administration',
  kitchen: 'Kitchen',
}
/** Kinds that carry a building record (address, manager, utilities). */
export const BUILDING_KINDS: readonly LocationKind[] = ['building', 'school', 'warehouse', 'depot', 'admin', 'stadium']
/** Kinds that only organise other nodes. */
export const STRUCTURAL_KINDS: readonly LocationKind[] = ['campus', 'floor', 'wing']

export const SPACE_TYPES = [
  'classroom',
  'office',
  'gym',
  'cafeteria',
  'kitchen',
  'restroom',
  'mechanical',
  'electrical',
  'library',
  'lab',
  'storage',
  'auditorium',
  'athletic',
  'exterior',
  'corridor',
  'other',
] as const
export type SpaceType = (typeof SPACE_TYPES)[number]
export const SPACE_TYPE_LABELS: Record<SpaceType, string> = {
  classroom: 'Classroom',
  office: 'Office',
  gym: 'Gym',
  cafeteria: 'Cafeteria',
  kitchen: 'Kitchen',
  restroom: 'Restroom',
  mechanical: 'Mechanical',
  electrical: 'Electrical',
  library: 'Library',
  lab: 'Lab',
  storage: 'Storage',
  auditorium: 'Auditorium',
  athletic: 'Athletic',
  exterior: 'Exterior',
  corridor: 'Corridor',
  other: 'Other',
}
/** Infrastructure spaces whose detail (equipment, documents, notes) only Facilities sees. */
export const SENSITIVE_SPACE_TYPES: readonly SpaceType[] = ['mechanical', 'electrical']

export const SPACE_STATUSES = ['available', 'restricted', 'closed', 'maintenance', 'construction', 'emergency_closure', 'retired'] as const
export type SpaceStatus = (typeof SPACE_STATUSES)[number]
export const SPACE_STATUS_LABELS: Record<SpaceStatus, string> = {
  available: 'Available',
  restricted: 'Restricted',
  closed: 'Closed',
  maintenance: 'Maintenance',
  construction: 'Construction',
  emergency_closure: 'Emergency closure',
  retired: 'Retired',
}
/** Statuses where the space cannot be used for what it is booked for. */
export const UNAVAILABLE_SPACE_STATUSES: readonly SpaceStatus[] = ['closed', 'maintenance', 'construction', 'emergency_closure']

export const CONDITIONS = ['good', 'fair', 'poor', 'critical'] as const
export type FacilityCondition = (typeof CONDITIONS)[number]
export const CONDITION_LABELS: Record<FacilityCondition, string> = { good: 'Good', fair: 'Fair', poor: 'Poor', critical: 'Critical' }

/** A secure document reference on a building, space or equipment (floor plan, warranty, permit…). */
export interface FacilityDocumentRef {
  id: string
  kind: 'floor_plan' | 'warranty' | 'manual' | 'permit' | 'inspection' | 'contract' | 'photo' | 'other'
  title: string
  blobPath: string | null
  fileName: string | null
  /** Security-sensitive (floor plans, alarm or camera layouts): Facilities managers only. */
  restricted: boolean
  uploadedAt: string
  uploadedByName: string
}

/** The district's location directory — Facilities owns it; every module points at it. */
export interface FacilityLocation extends BaseDocument {
  docType: 'facilityLocation'
  name: string
  kind: LocationKind
  schoolId: string | null
  parentId: string | null
  /** Printed on a QR label; scanning it opens the location's page. */
  code: string
  active: boolean
  // ── hierarchy ──
  /** The building this node sits in (itself, for a building). Kept current when nodes move. */
  buildingId?: string | null
  floor?: string | null
  wing?: string | null
  // ── space record ──
  spaceType?: SpaceType | null
  capacity?: number | null
  status?: SpaceStatus
  statusReason?: string | null
  statusSince?: string | null
  /** The closure currently holding this space, when there is one. */
  closureId?: string | null
  departmentName?: string | null
  accessibilityNotes?: string | null
  /** The Calendar/Sections room this space is — the link that lets room closures reach bookings. */
  roomId?: string | null
  /** Infrastructure detail is Facilities-only (mechanical/electrical rooms default to sensitive). */
  sensitive?: boolean
  condition?: FacilityCondition | null
  conditionNote?: string | null
  conditionAssessedAt?: string | null
  // ── building record ──
  address?: string | null
  buildingType?: string | null
  squareFootage?: number | null
  openedAt?: string | null
  retiredAt?: string | null
  principalStaffId?: string | null
  managerStaffId?: string | null
  managerName?: string | null
  emergencyContact?: string | null
  utilityRefs?: { utility: Utility; provider: string; account: string | null; meter: string | null }[]
  documents?: FacilityDocumentRef[]
  notes?: string | null
  version?: number
  externalSystemId?: string | null
  providerType?: string | null
  lastSyncedAt?: string | null
  providerStatus?: string | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Closures (room/area/building availability Facilities publishes)
// ─────────────────────────────────────────────────────────────────────────────

export const CLOSURE_KINDS = ['closed', 'restricted', 'maintenance', 'construction', 'emergency_closure'] as const
export type ClosureKind = (typeof CLOSURE_KINDS)[number]
export const CLOSURE_STATUS_FOR: Record<ClosureKind, SpaceStatus> = { closed: 'closed', restricted: 'restricted', maintenance: 'maintenance', construction: 'construction', emergency_closure: 'emergency_closure' }

/**
 * A space (or several) unavailable for a stated reason, from a stated time.
 * Calendar shows it as a conflict on affected bookings and never cancels
 * them; Operations reads it as readiness. Facilities reopens it.
 */
export interface FacilityClosure extends BaseDocument {
  docType: 'facilityClosure'
  schoolId: string | null
  buildingId: string | null
  locationIds: string[]
  /** Calendar room ids of the closed spaces (from the directory link). */
  roomIds: string[]
  locationNames: string[]
  kind: ClosureKind
  reason: string
  workOrderId: string | null
  workOrderNumber: string | null
  /** A planned maintenance window has a start in the future and an expected end. */
  planned: boolean
  startsAt: string
  expectedEndAt: string | null
  status: 'scheduled' | 'active' | 'ended'
  endedAt: string | null
  endedByName: string | null
  openedByName: string
  history: FacilityEvent[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Equipment / systems
// ─────────────────────────────────────────────────────────────────────────────

export const EQUIPMENT_TYPES = [
  'hvac_unit',
  'boiler',
  'chiller',
  'generator',
  'elevator',
  'fire_panel',
  'kitchen_freezer',
  'kitchen_equipment',
  'pump',
  'water_heater',
  'roof_system',
  'door_operator',
  'lighting',
  'scoreboard',
  'playground',
  'other',
] as const
export type EquipmentType = (typeof EQUIPMENT_TYPES)[number]
export const EQUIPMENT_TYPE_LABELS: Record<EquipmentType, string> = {
  hvac_unit: 'HVAC unit',
  boiler: 'Boiler',
  chiller: 'Chiller',
  generator: 'Generator',
  elevator: 'Elevator',
  fire_panel: 'Fire panel',
  kitchen_freezer: 'Kitchen freezer',
  kitchen_equipment: 'Kitchen equipment',
  pump: 'Pump',
  water_heater: 'Water heater',
  roof_system: 'Roof system',
  door_operator: 'Door operator',
  lighting: 'Lighting',
  scoreboard: 'Scoreboard',
  playground: 'Playground',
  other: 'Other',
}

export const EQUIPMENT_STATUSES = ['operational', 'limited', 'out_of_service', 'under_repair', 'retired'] as const
export type EquipmentStatus = (typeof EQUIPMENT_STATUSES)[number]
export const EQUIPMENT_STATUS_LABELS: Record<EquipmentStatus, string> = { operational: 'Operational', limited: 'Limited', out_of_service: 'Out of service', under_repair: 'Under repair', retired: 'Retired' }
export const DOWN_EQUIPMENT_STATUSES: readonly EquipmentStatus[] = ['out_of_service', 'under_repair']

/**
 * A system Facilities maintains. When the item is an organisational asset it
 * links `assetId` and reads manufacturer/model/serial from Assets — it never
 * keeps a second lifecycle record. Facilities owns the service history.
 */
export interface FacilityEquipment extends BaseDocument {
  docType: 'facilityEquipment'
  equipmentCode: string
  name: string
  equipmentType: EquipmentType
  schoolId: string | null
  buildingId: string | null
  locationId: string | null
  assetId: string | null
  manufacturer: string | null
  model: string | null
  serialNumber: string | null
  installedAt: string | null
  warranty: { provider: string | null; start: string | null; end: string | null; coverage: string | null; documentId: string | null } | null
  status: EquipmentStatus
  statusReason: string | null
  statusSince: string | null
  /** Out-of-service stretches, so downtime is a fact rather than an estimate. */
  outages: { from: string; to: string | null; status: EquipmentStatus; reason: string | null; workOrderId: string | null }[]
  condition: FacilityCondition | null
  sensitive: boolean
  /** Who consumes this equipment's status (cafeteria for a freezer, athletics for a scoreboard). */
  servesModules: string[]
  /** The vendor holding its service contract (Purchasing's vendor record is the source). */
  serviceVendorId?: string | null
  /** Latest run-hours reading, for operating-hours PM. */
  runHours?: number | null
  documents: FacilityDocumentRef[]
  notes: string | null
  version: number
  history: FacilityEvent[]
  externalSystemId?: string | null
  providerType?: string | null
  lastSyncedAt?: string | null
  providerStatus?: string | null
}

// ─────────────────────────────────────────────────────────────────────────────
// Settings
// ─────────────────────────────────────────────────────────────────────────────

export interface PriorityDefinition {
  key: WorkOrderPriority
  label: string
  meaning: string
  /** Minutes until someone must respond (assign/acknowledge). Null = no target. */
  responseMinutes: number | null
  /** Hours until the work should be done. Null = no target. */
  resolutionHours: number | null
}

export interface FacilitiesTeam {
  key: string
  label: string
  /** Staff on the team (Staff ids). */
  staffIds: string[]
  /** Or everyone holding these roles. */
  roles: string[]
  categories: string[]
}

export interface FacilityDueRule {
  key: string
  label: string
  /** Any of these narrows the rule; empty means any. */
  priorities: WorkOrderPriority[]
  categories: string[]
  impacts: OperationalImpact[]
  schoolIds: string[]
  responseMinutes: number | null
  resolutionHours: number | null
}

export interface FacilityEscalationRule {
  key: string
  label: string
  /** unassigned: nobody has it N minutes after creation; unresolved: still active N minutes after creation. */
  when: 'unassigned' | 'unresolved' | 'response_overdue' | 'resolution_overdue'
  afterMinutes: number
  priorities: WorkOrderPriority[]
  notifyRoles: string[]
  notifyStaffIds: string[]
  /** Raise an Operations issue as well. */
  raiseOperationsIssue: boolean
  active: boolean
}

export interface FacilitiesSettings {
  categories: { key: string; label: string; active: boolean; defaultTeam: string | null }[]
  priorities: PriorityDefinition[]
  impactLabels: Partial<Record<OperationalImpact, string>>
  teams: FacilitiesTeam[]
  dueRules: FacilityDueRule[]
  escalations: FacilityEscalationRule[]
  verification: {
    /** none: closes at work complete. requester: the requester verifies. supervisor: a manager verifies. */
    mode: 'none' | 'requester' | 'supervisor'
    /** Auto-close after this many days without a response — only when the district permits it. */
    autoCloseDays: number | null
    /** Categories that always need a supervisor's verification, whatever the mode. */
    supervisorCategories: string[]
  }
  duplicateWindowDays: number
  /** Optional blended labor rate per team (cents/hour). No rate, no labor cost — costs are never invented. */
  laborRates: { team: string; rateCents: number }[]
  /** Which modules a closure of each space type is announced to. */
  closureNotifyRoles: string[]
  workClasses: WorkClass[]
  /** Districtwide rules a school cannot override. */
  locked: { priorities: boolean; escalations: boolean; verification: boolean }
  schools: Record<string, { routing: { category: string; team: string }[]; contacts: { label: string; staffId: string | null; phone: string | null }[] }>
  /** Numbering. */
  numbering: { workOrderPrefix: string; requestPrefix: string }
}

export const DEFAULT_PRIORITIES: PriorityDefinition[] = [
  { key: 'critical', label: 'Critical', meaning: 'Life safety, building closure risk or a failure stopping school operations. Respond immediately.', responseMinutes: 15, resolutionHours: 8 },
  { key: 'urgent', label: 'Urgent', meaning: 'Significant disruption to instruction, food service or a room; respond the same morning.', responseMinutes: 60, resolutionHours: 24 },
  { key: 'high', label: 'High', meaning: 'Affects a room or a group of people but has a workaround.', responseMinutes: 240, resolutionHours: 72 },
  { key: 'normal', label: 'Normal', meaning: 'Ordinary repair with no operational effect.', responseMinutes: 1440, resolutionHours: 240 },
  { key: 'routine', label: 'Routine', meaning: 'Scheduled when convenient: cosmetic, improvement or low-use areas.', responseMinutes: null, resolutionHours: 720 },
]

export const DEFAULT_TEAMS: FacilitiesTeam[] = [
  { key: 'hvac', label: 'HVAC', staffIds: [], roles: [], categories: ['hvac'] },
  { key: 'electrical', label: 'Electrical', staffIds: [], roles: [], categories: ['electrical', 'fire_life_safety'] },
  { key: 'plumbing', label: 'Plumbing', staffIds: [], roles: [], categories: ['plumbing'] },
  { key: 'general', label: 'General Maintenance', staffIds: [], roles: ['facilities_staff'], categories: ['carpentry', 'painting', 'furniture', 'building_envelope', 'roofing', 'other', 'technology', 'safety', 'elevator'] },
  { key: 'grounds', label: 'Grounds', staffIds: [], roles: [], categories: ['grounds'] },
  { key: 'custodial', label: 'Custodial', staffIds: [], roles: [], categories: ['custodial', 'event_setup', 'pest_control'] },
  { key: 'security_hardware', label: 'Security Hardware', staffIds: [], roles: [], categories: ['security_hardware', 'doors_locks'] },
  { key: 'kitchen_equipment', label: 'Kitchen Equipment', staffIds: [], roles: [], categories: ['kitchen_equipment'] },
]

export const DEFAULT_FACILITIES_SETTINGS: FacilitiesSettings = {
  categories: WORK_ORDER_CATEGORIES.map((key) => ({ key, label: WORK_ORDER_CATEGORY_LABELS[key], active: true, defaultTeam: DEFAULT_TEAMS.find((t) => t.categories.includes(key))?.key ?? 'general' })),
  priorities: DEFAULT_PRIORITIES,
  impactLabels: {},
  teams: DEFAULT_TEAMS,
  dueRules: [],
  escalations: [
    { key: 'critical-unassigned', label: 'Critical issue unassigned after 10 minutes', when: 'unassigned', afterMinutes: 10, priorities: ['critical'], notifyRoles: ['facilities_manager'], notifyStaffIds: [], raiseOperationsIssue: false, active: true },
    { key: 'critical-unresolved', label: 'Critical issue unresolved after 30 minutes', when: 'unresolved', afterMinutes: 30, priorities: ['critical'], notifyRoles: ['operations_manager'], notifyStaffIds: [], raiseOperationsIssue: false, active: true },
  ],
  verification: { mode: 'requester', autoCloseDays: null, supervisorCategories: ['fire_life_safety', 'elevator'] },
  duplicateWindowDays: 14,
  laborRates: [],
  closureNotifyRoles: ['operations_manager', 'principal', 'school_admin'],
  workClasses: ['maintenance', 'capital', 'custodial', 'grounds', 'project'],
  locked: { priorities: true, escalations: true, verification: false },
  schools: {},
  numbering: { workOrderPrefix: 'WO', requestPrefix: 'FR' },
}

/** A number like people say it: WO-2027-004281. */
export const facilityNumber = (prefix: string, year: number, n: number, width = 6) => `${prefix}-${year}-${String(n).padStart(width, '0')}`
